import { useQuery } from "@apollo/client/react";
import { Box, Stack } from "@mui/material";
import { Button, Modal, Typography } from "@trimble/modus-cmx";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAccount } from "@/context/account";
import { useRequestProductPriceList } from "@/hooks/service/useRequestProductPriceList";
import { PRODUCT_PRICELIST_ACCOUNT_COUNTRIES } from "@/queries";
import ProductPricelistCountrySelect from "./ProductPricelistCountrySelect";
import ProductPricelistExportButton from "./ProductPricelistExportButton";

type ExportPriceListModalProps = {
  open: boolean;
  onClose: () => void;
};

function ExportPriceListModal({ open, onClose }: ExportPriceListModalProps) {
  const { t } = useTranslation();
  const { account } = useAccount();
  const [selectedCountryCode, setSelectedCountryCode] = useState<string | null>(
    null
  );
  const { data, loading: countriesLoading } = useQuery(
    PRODUCT_PRICELIST_ACCOUNT_COUNTRIES,
    {
      variables: { id: account.id },
      skip: !open || !account.id,
    }
  );
  const { loading: exportLoading, submitRequest } =
    useRequestProductPriceList();
  const countries = data?.account.billToCountries ?? [];
  const defaultCountryCode =
    countries.find((country) => country.isDefault)?.code ?? "";
  const countryCode = selectedCountryCode ?? defaultCountryCode;
  const countryOptions = countries.map((country) => ({
    value: country.code,
    label: country.name,
  }));

  const handleClose = () => {
    setSelectedCountryCode(null);
    onClose();
  };

  const handleExport = async () => {
    if (!countryCode) return;

    const succeeded = await submitRequest(countryCode);
    if (succeeded) {
      handleClose();
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      modalId="export-price-list-modal"
      data-testid="export-price-list-modal"
    >
      <div slot="header">
        <Typography hierarchy="h4" size="lg" weight="bold">
          {t("product_pricelist.export_modal_title")}
        </Typography>
      </div>
      <div slot="content">
        <Stack spacing={2}>
          <Typography hierarchy="h5" size="md">
            {t("product_pricelist.export_modal_description")}
          </Typography>
          <Box>
            <ProductPricelistCountrySelect
              countryCode={countryCode}
              options={countryOptions}
              onCountryChange={setSelectedCountryCode}
              loading={countriesLoading}
              fullWidth
              label={t("product_pricelist.select_country")}
              required
            />
          </Box>
        </Stack>
      </div>
      <div
        slot="footer"
        style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}
      >
        <Button
          color="neutral"
          size="sm"
          shape="rectangle"
          variant="outlined"
          disabled={exportLoading}
          onButtonClick={handleClose}
        >
          {t("product_pricelist.cancel")}
        </Button>
        <ProductPricelistExportButton
          testId="product-pricelist-modal-export"
          label={t("product_pricelist.export")}
          loading={exportLoading}
          disabled={countriesLoading || !countryCode}
          onExport={() => {
            void handleExport();
          }}
        />
      </div>
    </Modal>
  );
}

export default ExportPriceListModal;
